import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import './Dashboard.css';

interface UserBalance {
  id: number;
  name: string;
  total_cash: number;
  updated_at: string;
}

interface ReconciliationDetails {
  ok?: boolean;
  expected?: number;
  entered?: number;
  discrepancy?: number;
  details?: {
    previous_close: number;
    clover_cash_sales: number;
    withdrawals: number;
    spending: number;
    cash_additions?: number;
    clover_payment_count: number;
    tolerance: number;
  };
  skipped?: boolean;
  reason?: string;
}

interface Transaction {
  id: number;
  transaction_id: string;
  type: string;
  user_name?: string;
  amount: number;
  recipient_name?: string;
  withdrawal_reason?: string;
  spending_description?: string;
  spending_category?: string;
  cash_source?: 'cash_payment' | 'bank_withdrawal';
  cash_destination?: 'till' | 'user';
  cash_description?: string;
  recorded_by_name?: string;
  created_at: string;
  // Daily closing fields
  bills_100?: number;
  bills_50?: number;
  bills_20?: number;
  bills_10?: number;
  bills_5?: number;
  coins_toonies?: number;
  coins_loonies?: number;
  coins_quarters?: number;
  coins_dimes?: number;
  coins_nickels?: number;
  coins_pennies?: number;
  reconciliation_status?: 'ok' | 'mismatch' | 'skipped' | null;
  reconciliation_details?: ReconciliationDetails | null;
}

interface CashBalance {
  total_till_cash: number;
  last_updated: string;
}

const Dashboard: React.FC = () => {
  const { user, sessionToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useNotification();
  const [userBalances, setUserBalances] = useState<UserBalance[]>([]);
  const [recentTransactions, setRecentTransactions] = useState<Transaction[]>([]);
  const [tillBalance, setTillBalance] = useState<CashBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const isManagerOrAdmin = !!user && ['Owner', 'Admin', 'Manager'].includes(user.role);
  const isEmployee = user?.role === 'Employee';

  const fetchData = async () => {
    try {
      setError('');

      const txParams = isManagerOrAdmin
        ? 'months=3'
        : 'limit=10';
      const txUrl = typeFilter !== 'all'
        ? `/api/transactions?${txParams}&type=${typeFilter}`
        : `/api/transactions?${txParams}`;

      const authedFetch = (url: string) =>
        fetch(url, {
          cache: 'no-store',
          headers: { 'X-Session-Token': sessionToken || '' },
        });

      const [balancesResponse, transactionsResponse, tillResponse] = await Promise.all([
        authedFetch('/api/user-balances'),
        authedFetch(txUrl),
        authedFetch('/api/cash-balance'),
      ]);

      if (!balancesResponse.ok || !transactionsResponse.ok || !tillResponse.ok) {
        throw new Error('Failed to fetch data');
      }

      const [balances, transactions, till] = await Promise.all([
        balancesResponse.json(),
        transactionsResponse.json(),
        tillResponse.json()
      ]);

      setUserBalances(balances);
      setRecentTransactions(transactions);
      setTillBalance(till);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const toastState = (location.state as { toast?: { type: 'success' | 'error' | 'info'; title?: string; message: string } })?.toast;
    if (toastState) {
      showToast({ type: toastState.type, title: toastState.title ?? 'Success', message: toastState.message });
      navigate(location.pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchData();

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchData();
      }
    };

    const handleFocus = () => {
      fetchData();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'daily_closing':
        return '🏪';
      case 'cash_withdrawal':
        return '💰';
      case 'cash_deposit':
        return '💳';
      case 'cash_spending':
        return '🛒';
      default:
        return '📝';
    }
  };

  const getTransactionTitle = (transaction: Transaction) => {
    switch (transaction.type) {
      case 'daily_closing':
        return `Daily Closing${transaction.recipient_name ? ` - ${transaction.recipient_name}` : ''}`;
      case 'cash_withdrawal':
        return `Cash Withdrawal - ${transaction.recipient_name}`;
      case 'cash_deposit':
        return transaction.cash_source ? `Cash Added — ${transaction.cash_source === 'bank_withdrawal' ? 'Bank withdrawal' : 'Cash payment'}` : 'Cash Deposit';
      case 'cash_spending':
        return `Cash Spending - ${transaction.user_name}`;
      default:
        return 'Transaction';
    }
  };

  const totalUserCash = userBalances.reduce((sum, user) => sum + (Number(user.total_cash) || 0), 0);

  const groupTransactionsByMonth = (transactions: Transaction[]) => {
    const groups: { [key: string]: Transaction[] } = {};
    transactions.forEach((t) => {
      const date = new Date(t.created_at);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(t);
    });
    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([key, txns]) => {
        const [year, month] = key.split('-');
        const label = new Date(parseInt(year), parseInt(month) - 1).toLocaleString('en-CA', { month: 'long', year: 'numeric' });
        const total = txns.reduce((sum, t) => sum + Number(t.amount), 0);
        return { label, transactions: txns, total };
      });
  };

  const renderTransactionDetails = () => {
    if (!selectedTransaction) return null;

    return (
      <div className="modal-overlay" onClick={() => setSelectedTransaction(null)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>{getTransactionTitle(selectedTransaction)}</h3>
            <button className="close-btn" onClick={() => setSelectedTransaction(null)}>×</button>
          </div>
          <div className="modal-body">
            <div className="detail-row">
              <strong>Amount:</strong>
              <span>{formatCurrency(selectedTransaction.amount)}</span>
            </div>

            <div className="detail-row">
              <strong>Date:</strong>
              <span>{formatDate(selectedTransaction.created_at)}</span>
            </div>

            {selectedTransaction.reconciliation_status === 'mismatch' && selectedTransaction.reconciliation_details?.details && (
              <div style={{ background: '#fff5f5', borderLeft: '4px solid #d32f2f', padding: '0.75rem 1rem', margin: '0.75rem 0' }}>
                <h4 style={{ margin: '0 0 0.5rem', color: '#d32f2f' }}>⚠ Cash Mismatch</h4>
                {(() => {
                  const r = selectedTransaction.reconciliation_details!;
                  const d = r.details!;
                  const sign = (r.discrepancy ?? 0) >= 0 ? '+' : '';
                  return (
                    <div style={{ fontSize: '0.9em' }}>
                      <div>Previous close: {formatCurrency(d.previous_close)}</div>
                      <div>Clover cash sales: +{formatCurrency(d.clover_cash_sales)} ({d.clover_payment_count} payments)</div>
                      <div>Cash added to till: +{formatCurrency(d.cash_additions || 0)}</div>
                      <div>Withdrawals from till: -{formatCurrency(d.withdrawals)}</div>
                      <div>Spending from till: -{formatCurrency(d.spending)}</div>
                      <div style={{ marginTop: 4 }}><strong>Expected:</strong> {formatCurrency(r.expected ?? 0)}</div>
                      <div><strong>Entered:</strong> {formatCurrency(r.entered ?? 0)}</div>
                      <div style={{ marginTop: 4, color: '#d32f2f' }}>
                        <strong>Discrepancy: {sign}{formatCurrency(r.discrepancy ?? 0)}</strong>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {selectedTransaction.type === 'daily_closing' && (
              <>
                {selectedTransaction.recipient_name && (
                  <div className="detail-row">
                    <strong>Counted by:</strong>
                    <span>{selectedTransaction.recipient_name}</span>
                  </div>
                )}

                <div className="cash-breakdown">
                  <h4>Bills Breakdown</h4>
                  <div className="breakdown-grid">
                    {selectedTransaction.bills_100 ? (
                      <div className="breakdown-item">
                        <span>$100 × {selectedTransaction.bills_100}</span>
                        <span>{formatCurrency(selectedTransaction.bills_100 * 100)}</span>
                      </div>
                    ) : null}
                    {selectedTransaction.bills_50 ? (
                      <div className="breakdown-item">
                        <span>$50 × {selectedTransaction.bills_50}</span>
                        <span>{formatCurrency(selectedTransaction.bills_50 * 50)}</span>
                      </div>
                    ) : null}
                    {selectedTransaction.bills_20 ? (
                      <div className="breakdown-item">
                        <span>$20 × {selectedTransaction.bills_20}</span>
                        <span>{formatCurrency(selectedTransaction.bills_20 * 20)}</span>
                      </div>
                    ) : null}
                    {selectedTransaction.bills_10 ? (
                      <div className="breakdown-item">
                        <span>$10 × {selectedTransaction.bills_10}</span>
                        <span>{formatCurrency(selectedTransaction.bills_10 * 10)}</span>
                      </div>
                    ) : null}
                    {selectedTransaction.bills_5 ? (
                      <div className="breakdown-item">
                        <span>$5 × {selectedTransaction.bills_5}</span>
                        <span>{formatCurrency(selectedTransaction.bills_5 * 5)}</span>
                      </div>
                    ) : null}
                  </div>

                  <h4>Coins Breakdown</h4>
                  <div className="breakdown-grid">
                    {selectedTransaction.coins_toonies ? (
                      <div className="breakdown-item">
                        <span>Toonies × {selectedTransaction.coins_toonies}</span>
                        <span>{formatCurrency(selectedTransaction.coins_toonies * 2)}</span>
                      </div>
                    ) : null}
                    {selectedTransaction.coins_loonies ? (
                      <div className="breakdown-item">
                        <span>Loonies × {selectedTransaction.coins_loonies}</span>
                        <span>{formatCurrency(selectedTransaction.coins_loonies * 1)}</span>
                      </div>
                    ) : null}
                    {selectedTransaction.coins_quarters ? (
                      <div className="breakdown-item">
                        <span>Quarters × {selectedTransaction.coins_quarters}</span>
                        <span>{formatCurrency(selectedTransaction.coins_quarters * 0.25)}</span>
                      </div>
                    ) : null}
                    {selectedTransaction.coins_dimes ? (
                      <div className="breakdown-item">
                        <span>Dimes × {selectedTransaction.coins_dimes}</span>
                        <span>{formatCurrency(selectedTransaction.coins_dimes * 0.10)}</span>
                      </div>
                    ) : null}
                    {selectedTransaction.coins_nickels ? (
                      <div className="breakdown-item">
                        <span>Nickels × {selectedTransaction.coins_nickels}</span>
                        <span>{formatCurrency(selectedTransaction.coins_nickels * 0.05)}</span>
                      </div>
                    ) : null}
                    {selectedTransaction.coins_pennies ? (
                      <div className="breakdown-item">
                        <span>Pennies × {selectedTransaction.coins_pennies}</span>
                        <span>{formatCurrency(selectedTransaction.coins_pennies * 0.01)}</span>
                      </div>
                    ) : null}
                  </div>
                </div>
              </>
            )}

            {selectedTransaction.type === 'cash_withdrawal' && (
              <>
                <div className="detail-row">
                  <strong>Recipient:</strong>
                  <span>{selectedTransaction.recipient_name}</span>
                </div>
                {selectedTransaction.withdrawal_reason && (
                  <div className="detail-row">
                    <strong>Reason:</strong>
                    <span>{selectedTransaction.withdrawal_reason}</span>
                  </div>
                )}
              </>
            )}

            {selectedTransaction.type === 'cash_deposit' && selectedTransaction.cash_source && (
              <>
                <div className="detail-row"><strong>Source:</strong><span>{selectedTransaction.cash_source === 'bank_withdrawal' ? 'Bank withdrawal' : 'Cash payment'}</span></div>
                <div className="detail-row"><strong>Added to:</strong><span>{selectedTransaction.cash_destination === 'till' ? 'Till' : selectedTransaction.recipient_name}</span></div>
                <div className="detail-row"><strong>Recorded by:</strong><span>{selectedTransaction.recorded_by_name}</span></div>
                <div className="detail-row"><strong>Details:</strong><span>{selectedTransaction.cash_description}</span></div>
              </>
            )}

            {selectedTransaction.type === 'cash_spending' && (
              <>
                <div className="detail-row">
                  <strong>Person:</strong>
                  <span>{selectedTransaction.user_name}</span>
                </div>
                {selectedTransaction.spending_category && (
                  <div className="detail-row">
                    <strong>Category:</strong>
                    <span>{selectedTransaction.spending_category}</span>
                  </div>
                )}
                {selectedTransaction.spending_description && (
                  <div className="detail-row">
                    <strong>Description:</strong>
                    <span>{selectedTransaction.spending_description}</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="dashboard loading">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard error">
        <div className="error-message">
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={fetchData} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-top-bar">
        <button
          onClick={() => navigate('/cash/new')}
          className="new-transaction-btn"
        >
          Other cash actions
        </button>
        <div className="cs-actions"><button onClick={() => navigate('/cash/add')}>Add Cash</button><button onClick={() => navigate('/cash/daily-closing')}>Close till</button><button onClick={() => navigate('/cash/withdrawal')}>Take cash</button><button onClick={() => navigate('/cash/spending')}>Record spending</button></div>
      </div>

      <div className="stats-grid">
        <div className="stat-card till-balance">
          <div className="stat-icon">🏪</div>
          <div className="stat-content">
            <h3>Till Balance</h3>
            <p className="stat-value">{tillBalance ? formatCurrency(Number(tillBalance.total_till_cash)) : '$0.00'}</p>
            <small>Last updated: {tillBalance ? formatDate(tillBalance.last_updated) : 'Never'}</small>
          </div>
        </div>

        {isEmployee ? (
          <div className="stat-card user-cash">
            <div className="stat-icon">👤</div>
            <div className="stat-content">
              <h3>My Cash</h3>
              <p className="stat-value">{formatCurrency(totalUserCash)}</p>
              <small>Cash currently held by you</small>
            </div>
          </div>
        ) : (
          <>
            <div className="stat-card user-cash">
              <div className="stat-icon">👥</div>
              <div className="stat-content">
                <h3>Cash with Users</h3>
                <p className="stat-value">{formatCurrency(totalUserCash)}</p>
                <small>{userBalances.length} users have cash</small>
              </div>
            </div>

            <div className="stat-card total-cash">
              <div className="stat-icon">💰</div>
              <div className="stat-content">
                <h3>Total Cash</h3>
                <p className="stat-value">
                  {formatCurrency((Number(tillBalance?.total_till_cash) || 0) + totalUserCash)}
                </p>
                <small>Till + User cash</small>
              </div>
            </div>
          </>
        )}
      </div>

      {!isEmployee && userBalances.length > 0 && (
        <div className="section">
          <h2>User Cash Balances</h2>
          <div className="user-balances">
            {userBalances.map((user) => (
              <div key={user.id} className="user-balance-card">
                <div className="user-info">
                  <h4>{user.name}</h4>
                  <p className="balance">{formatCurrency(Number(user.total_cash) || 0)}</p>
                </div>
                <div className="last-updated">
                  <small>Updated: {formatDate(user.updated_at)}</small>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="section">
        <div className="section-header">
          <h2>{isManagerOrAdmin ? 'Transaction History (3 Months)' : 'Recent Transactions'}</h2>
          <div className="header-actions">
            {lastUpdated && (
              <small className="last-updated" style={{ marginRight: '10px', color: '#666' }}>
                Updated {formatDate(lastUpdated.toISOString())}
              </small>
            )}
            {isManagerOrAdmin && (
              <button onClick={() => navigate('/users')} className="refresh-btn" style={{ marginRight: '10px' }}>
                👥 Manage Users
              </button>
            )}
            <button onClick={fetchData} className="refresh-btn">
              🔄 Refresh
            </button>
          </div>
        </div>

        {isManagerOrAdmin && (
          <div className="transaction-filters">
            {['all', 'daily_closing', 'cash_deposit', 'cash_withdrawal', 'cash_spending'].map((t) => (
              <button
                key={t}
                className={`filter-btn ${typeFilter === t ? 'active' : ''}`}
                onClick={() => setTypeFilter(t)}
              >
                {t === 'all' ? 'All' : t === 'daily_closing' ? '🏪 Closings' : t === 'cash_deposit' ? '＋ Cash added' : t === 'cash_withdrawal' ? '💰 Withdrawals' : '🛒 Spending'}
              </button>
            ))}
            <span className="transaction-count">{recentTransactions.length} transactions</span>
          </div>
        )}

        {recentTransactions.length === 0 ? (
          <div className="no-transactions">
            <p>No transactions found.</p>
          </div>
        ) : isManagerOrAdmin ? (
          groupTransactionsByMonth(recentTransactions).map((group) => (
            <div key={group.label} className="month-group">
              <div className="month-header">
                <h3>{group.label}</h3>
                <span className="month-total">{group.transactions.length} txns &middot; {formatCurrency(group.total)}</span>
              </div>
              <div className="transactions-list">
                {group.transactions.map((transaction) => (
                  <div
                    key={transaction.id}
                    className="transaction-card"
                    onClick={() => setSelectedTransaction(transaction)}
                    style={transaction.reconciliation_status === 'mismatch' ? { borderLeft: '4px solid #d32f2f', background: '#fff5f5' } : undefined}
                  >
                    <div className="transaction-icon">
                      {getTransactionIcon(transaction.type)}
                    </div>
                    <div className="transaction-content">
                      <h4>
                        {getTransactionTitle(transaction)}
                        {transaction.reconciliation_status === 'mismatch' && (
                          <span style={{ marginLeft: 8, fontSize: '0.75em', background: '#d32f2f', color: '#fff', padding: '2px 6px', borderRadius: 4 }}>
                            ⚠ MISMATCH
                          </span>
                        )}
                      </h4>
                      {transaction.user_name && (
                        <p className="transaction-user">User: {transaction.user_name}</p>
                      )}
                      <p className="transaction-amount">{transaction.type === 'cash_deposit' ? '+' : ''}{formatCurrency(transaction.amount)}</p>
                      {transaction.cash_description && <p className="transaction-reason">{transaction.recipient_name}: {transaction.cash_description}</p>}
                      {transaction.withdrawal_reason && (
                        <p className="transaction-reason">{transaction.withdrawal_reason}</p>
                      )}
                      {transaction.spending_description && (
                        <p className="transaction-reason">
                          {transaction.spending_description}
                          {transaction.spending_category && ` (${transaction.spending_category})`}
                        </p>
                      )}
                      <small className="transaction-date">{formatDate(transaction.created_at)}</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : (
          <div className="transactions-list">
            {recentTransactions.map((transaction) => (
              <div
                key={transaction.id}
                className="transaction-card"
                onClick={() => setSelectedTransaction(transaction)}
              >
                <div className="transaction-icon">
                  {getTransactionIcon(transaction.type)}
                </div>
                <div className="transaction-content">
                  <h4>{getTransactionTitle(transaction)}</h4>
                  {transaction.user_name && (
                    <p className="transaction-user">User: {transaction.user_name}</p>
                  )}
                  <p className="transaction-amount">{transaction.type === 'cash_deposit' ? '+' : ''}{formatCurrency(transaction.amount)}</p>
                  {transaction.cash_description && <p className="transaction-reason">{transaction.recipient_name}: {transaction.cash_description}</p>}
                  {transaction.withdrawal_reason && (
                    <p className="transaction-reason">{transaction.withdrawal_reason}</p>
                  )}
                  {transaction.spending_description && (
                    <p className="transaction-reason">
                      {transaction.spending_description}
                      {transaction.spending_category && ` (${transaction.spending_category})`}
                    </p>
                  )}
                  <small className="transaction-date">{formatDate(transaction.created_at)}</small>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {renderTransactionDetails()}
    </div>
  );
};

export default Dashboard;
